import React, { useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";
import IndiaMap from "react-datamaps-india";
import { useQuery } from "react-query";
import {
  getMonthWiseStats,
  getDayWiseStats,
} from "../../services/analyticsService";
import { formatAmount } from "../../utils/formatters";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { subMonths, startOfMonth, subDays } from "date-fns";
import { capitalize } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

const toLocalMidnight = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getInitialStartDate = (timeRange, endDate = new Date()) => {
  if (timeRange === "month") {
    return toLocalMidnight(
      new Date(endDate.getFullYear(), endDate.getMonth() - 2, 1) // 3 months (current month + 2 previous months)
    );
  } else {
    const d = new Date(endDate);
    d.setDate(d.getDate() - 90); // 3 months (90 days)
    return toLocalMidnight(d);
  }
};

const StateWiseSalesTrend = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const STORAGE_KEY = "filters:state-wise";
  let savedFilters = null;
  try {
    savedFilters = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch (_) { }
  const TODAY_KEY = new Date().toLocaleDateString("en-CA");
  if (savedFilters && savedFilters.dateKey !== TODAY_KEY) {
    savedFilters = null;
  }

  // Initialize timeRange first (no hourly - only month or day)
  const rawInitialTimeRange = location.state?.timeRange || savedFilters?.timeRange || "month";
  const initialTimeRange = rawInitialTimeRange === "hour" ? "day" : rawInitialTimeRange;

  const [timeRange, setTimeRange] = useState(initialTimeRange);
  const [endDate, setEndDate] = useState(() => {
    if (location.state?.endDate) {
      return toLocalMidnight(new Date(location.state.endDate));
    }
    if (savedFilters?.endDate) {
      return toLocalMidnight(new Date(savedFilters.endDate));
    }
    return toLocalMidnight(new Date());
  });
  const [startDate, setStartDate] = useState(() => {
    if (location.state?.startDate) {
      return toLocalMidnight(new Date(location.state.startDate));
    }
    if (savedFilters?.startDate) {
      return toLocalMidnight(new Date(savedFilters.startDate));
    }
    return toLocalMidnight(getInitialStartDate(initialTimeRange, new Date()));
  });
  const [showQuantity, setShowQuantity] = useState(
    location.state?.showQuantity ?? savedFilters?.showQuantity ?? false
  );
  const [showAllStates, setShowAllStates] = useState(
    location.state?.showAllStates ?? savedFilters?.showAllStates ?? false
  );
  const [postingAccount, setPostingAccount] = useState(
    location.state?.postingAccount || savedFilters?.postingAccount || ""
  );
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          dateKey: TODAY_KEY,
          startDate: startDate?.toISOString?.() || startDate,
          endDate: endDate?.toISOString?.() || endDate,
          timeRange,
          showQuantity,
          showAllStates,
          postingAccount,
        })
      );
    } catch (_) { }
  }, [
    startDate,
    endDate,
    timeRange,
    showQuantity,
    showAllStates,
    postingAccount,
  ]);

  useEffect(() => {
    if (toLocalMidnight(endDate) < startDate) {
      setStartDate(getInitialStartDate(timeRange, endDate));
    }
  }, [timeRange, endDate]);

  // Track previous timeRange to detect when switching
  const prevTimeRangeRef = useRef(timeRange);
  
  // Adjust dates when switching time ranges
  useEffect(() => {
    // Only adjust when switching TO a different time range
    if (prevTimeRangeRef.current === timeRange) {
      prevTimeRangeRef.current = timeRange;
      return;
    }

    if (timeRange === "month" && endDate) {
      const endDateMonth = endDate.getMonth();
      const endDateYear = endDate.getFullYear();
      
      // Always set start date to 1st of the month when in monthly view
      const firstDayOfMonth = new Date(endDateYear, endDateMonth, 1);
      setStartDate(toLocalMidnight(firstDayOfMonth));
      
      // Set end date to last day of the month
      const lastDayOfMonth = new Date(endDateYear, endDateMonth + 1, 0);
      setEndDate(toLocalMidnight(lastDayOfMonth));
    }
    
    // Update the ref for next comparison
    prevTimeRangeRef.current = timeRange;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]); // Only run when timeRange changes

  const handleStartDateChange = (date) => {
    if (timeRange === "month") {
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      setStartDate(toLocalMidnight(firstDay));
    } else {
      setStartDate(toLocalMidnight(date));
    }
  };

  const handleEndDateChange = (date) => {
    if (timeRange === "month") {
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      setEndDate(toLocalMidnight(lastDay));
      const newStartDate = new Date(date.getFullYear(), date.getMonth() - 2, 1);
      if (toLocalMidnight(date) < startDate) {
        setStartDate(toLocalMidnight(newStartDate));
      }
    } else {
      setEndDate(toLocalMidnight(date));
      if (toLocalMidnight(date) < startDate) {
        setStartDate(getInitialStartDate(timeRange, date));
      }
    }
  };

  const {
    data: salesData,
    isLoading: isSalesLoading,
    error: salesError,
  } = useQuery(
    ["stateSalesData", timeRange, startDate, endDate, postingAccount],
    () => {
      // Format dates - backend stats endpoints expect YYYY-MM-DD format for all time ranges
      // Helper function to format date as YYYY-MM-DD
      const formatDateToYYYYMMDD = (date) => {
        if (!date) return null;
        let dateObj = date;
        if (!(date instanceof Date)) {
          dateObj = new Date(date);
        }
        if (isNaN(dateObj.getTime())) return null;
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const formattedStartDate = formatDateToYYYYMMDD(startDate);
      const formattedEndDate = formatDateToYYYYMMDD(endDate);

      if (!formattedStartDate || !formattedEndDate) {
        throw new Error("Invalid date format");
      }

      const params = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        statType: "state",
        groupBy: "day",
        ...(postingAccount && { postingAccount }),
      };
      return timeRange === "month"
        ? getMonthWiseStats(params)
        : getDayWiseStats(params);
    },
    {
      onError: (error) => {
        console.log("State Sales Data Error:", error);
        toast.error(error.message || "Failed to load state sales data");
      },
    }
  );


  const formatCell = (data) => {
    if (!data) return showQuantity ? "0" : "₹0";
    return showQuantity
      ? `${data.qty}`
      : `${formatAmount(data.net_amount ?? 0)}`;
  };

  // Export (XLSX) – aligned with ZoneWiseSalesTrend / PortalWiseSalesTrend
  const handleExportToCSV = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({
        startMonth: startDate.toLocaleDateString("en-CA").split("T")[0],
        endMonth: endDate.toLocaleDateString("en-CA").split("T")[0],
        qtyReq: showQuantity,
        statType: "state",
        exportToCsv: "true",
        ...(postingAccount && { postingAccount }),
      });

      const baseURL =
        process.env.REACT_APP_API_BASE_URL ||
        "https://digitalapi.palsonsderma.com";
      const response = await fetch(
        `${baseURL}/data/order/${timeRange === "month" ? "month-wise-stats" : "day-wise-stats"}?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      const contentDisposition = response.headers.get("Content-Disposition");
      const match = contentDisposition?.match(/filename[*]?=(?:UTF-8'')?"?([^";\n]+)"?/i);
      const filename =
        match?.[1]?.trim().replace(/^["']|["']$/g, "")
          ? decodeURIComponent(match[1].trim().replace(/^["']|["']$/g, ""))
          : `state-wise-sales-${startDate.toLocaleDateString("en-CA").split("T")[0]}-to-${endDate.toLocaleDateString("en-CA").split("T")[0]}${postingAccount ? `-${postingAccount}` : ""}.xlsx`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download export");
    } finally {
      setIsExporting(false);
    }
  };

  // Prepare data for charts
  // const prepareChartData = () => {
  //   // console.log("salesData:", salesData);
  //   if (!salesData) return [];

  //   // Filter out totals and get only state data
  //   const stateData = salesData.filter((sale) => {
  //     const name = timeRange === "month" ? sale.state : sale.stat_value;
  //     // console.log("Checking sale:", sale, "name:", name);
  //     return name && !name.toLowerCase().includes("total");
  //   });

  //   // console.log("Filtered stateData:", stateData);

  //   const totalAmount = stateData.reduce((sum, sale) => {
  //     let amount = 0;
  //     if (timeRange === "month") {
  //       // For monthly view, use total object
  //       amount = showQuantity
  //         ? sale.total?.qty || 0
  //         : sale.total?.net_amount || 0;
  //     } else {
  //       // For daily view, use total_net_amount and total_qty
  //       amount = showQuantity
  //         ? sale.total_qty || 0
  //         : sale.total_net_amount || 0;
  //     }
  //     return sum + amount;
  //   }, 0);

  //   // console.log("totalAmount:", totalAmount);

  //   const chartData = stateData.map((sale) => {
  //     let amount = 0;
  //     if (timeRange === "month") {
  //       // For monthly view, use total object
  //       amount = showQuantity
  //         ? sale.total?.qty || 0
  //         : sale.total?.net_amount || 0;
  //     } else {
  //       // For daily view, use total_net_amount and total_qty
  //       amount = showQuantity
  //         ? sale.total_qty || 0
  //         : sale.total_net_amount || 0;
  //     }

  //     const percentage =
  //       totalAmount > 0 ? ((amount / totalAmount) * 100).toFixed(1) : 0;

  //     const chartItem = {
  //       name: timeRange === "month" ? sale.state : sale.stat_value,
  //       value: amount,
  //       percentage: parseFloat(percentage),
  //       formattedValue: showQuantity ? amount.toString() : formatAmount(amount),
  //     };
  //     // console.log("Chart item:", chartItem);
  //     return chartItem;
  //   });

  //   // Sort by value in descending order for better visualization
  //   const sortedChartData = chartData
  //     .filter((item) => item.value > 0) // Only show states with data
  //     .sort((a, b) => b.value - a.value);

  //   // console.log("Final chartData:", sortedChartData);

  //   return sortedChartData;
  // };
  // Prepare data for charts
  const prepareChartData = () => {
    //console.log("stateWiseSalesTrend - salesData:", salesData);
    if (!salesData) return { barData: [], pieData: [] };

    // Filter out totals, N/A values and get only portal data
    const portalData = salesData.filter((sale) => {
      const name = sale.state || sale.stat_value;
      //console.log("Checking state:", name, "sale:", sale);
      // Filter out "total", "grand total", "N/A", and "n/a" (case insensitive)
      const lowerName = name.toLowerCase();
      return name &&
        lowerName !== "total" &&
        lowerName !== "grand total" &&
        lowerName !== "n/a" &&
        lowerName !== "N/A" &&
        lowerName !== "na";
    });

    //console.log("Filtered portalData:", portalData);

    const totalAmount = portalData.reduce((sum, sale) => {
      let amount = 0;
      if (timeRange === "month") {
        // For monthly view, use total object
        amount = showQuantity
          ? sale.total?.qty || 0
          : sale.total?.net_amount || 0;
      } else {
        // For daily view, use total_net_amount and total_qty
        amount = showQuantity
          ? sale.total_qty || 0
          : sale.total_net_amount || 0;
      }
      return sum + amount;
    }, 0);

    //console.log("totalAmount:", totalAmount);

    const chartData = portalData.map((sale) => {
      let amount = 0;
      if (timeRange === "month") {
        // For monthly view, use total object
        amount = showQuantity
          ? sale.total?.qty || 0
          : sale.total?.net_amount || 0;
      } else {
        // For daily view, use total_net_amount and total_qty
        amount = showQuantity
          ? sale.total_qty || 0
          : sale.total_net_amount || 0;
      }

      const percentage =
        totalAmount > 0 ? ((amount / totalAmount) * 100).toFixed(1) : 0;

      const chartItem = {
        name: sale.state || sale.stat_value,
        value: amount,
        percentage: parseFloat(percentage),
        formattedValue: showQuantity ? amount.toString() : formatAmount(amount),
      };
      //console.log("Chart item:", chartItem);
      return chartItem;
    });

    //console.log("Final chartData:", chartData);

    return {
      barData: chartData,
      pieData: chartData,
    };
  };

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

  const renderHorizontalBarLabel = ({ x, y, width, height, value }) => {
    if (value == null) return null;
    const label = showQuantity ? `${value}` : formatAmount(value);
    const centerY = y + (height || 0) / 2;

    // Check if bar is too small to show label above (less than 30px width)
    const isBarTooSmall = (width || 0) < 30;

    let labelX, labelY;

    if (isBarTooSmall) {
      // Position label to the right of the bar when bar is too small
      labelX = (x || 0) + (width || 0) + (showQuantity ? 45 : 30);
      labelY = centerY;
    } else {
      // Position label inside the bar when bar is large enough
      labelX = (x || 0) + (width || 0) / 2; // Center of the bar
      labelY = centerY; // Inside the bar
    }

    return (
      <text
        x={labelX}
        y={labelY}
        fill={isBarTooSmall ? "#6b7280" : "white"}
        fontSize={12}
        fontWeight="bold"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {label}
      </text>
    );
  };

  const renderHorizontalBarChart = () => {
    const { barData } = prepareChartData();

    // Sort by value descending for top states
    const sortedBarData = [...barData].sort((a, b) => b.value - a.value);
    const visibleBarData = showAllStates
      ? sortedBarData
      : sortedBarData.slice(0, 10);
    const chartHeight = Math.max(400, visibleBarData.length * 32);

    // Don't render if no data
    if (!barData || barData.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">
            State Wise Sales
          </h3>
          <div className="flex items-center justify-center h-64 text-gray-500">
            No data available for the selected time range
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">
          State Wise Sales Revenue
        </h3>

        <div className="relative overflow-visible">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={visibleBarData}
              layout="vertical"
              margin={{ top: 10, right: 120, left: 0, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" />
              <YAxis
                dataKey="name"
                type="category"
                width={100}
                tick={{
                  fontSize: 11,
                  fill: "#374151",
                }}
                tickFormatter={(value) =>
                  typeof value === "string"
                    ? value.charAt(0).toUpperCase() + value.slice(1)
                    : value
                }
                interval={0}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(value) => [
                  showQuantity ? `${value}` : formatAmount(value),
                  showQuantity ? "Quantity" : "Amount",
                ]}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  maxWidth: 280,
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                wrapperStyle={{ maxWidth: 280 }}
                labelStyle={{
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                cursor={{
                  fill: showQuantity
                    ? "rgba(139, 92, 246, 0.08)"
                    : "rgba(59, 130, 246, 0.1)",
                }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                {visibleBarData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      showQuantity
                        ? `hsl(${265 + index * 5}, 70%, 60%)`
                        : `hsl(${220 + index * 7}, 80%, 60%)`
                    }
                  />
                ))}
                <LabelList
                  content={renderHorizontalBarLabel}

                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {barData.length > 10 && (
          <div className="flex justify-center mt-4">
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setShowAllStates((prev) => !prev)}
            >
              {showAllStates ? "Show Less" : "View More"}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderIndiaMap = () => {
    // console.log("Rendering India Map with salesData:", salesData);
    if (!salesData) return null;

    // State name mapping to match India map expectations
    const stateNameMapping = {
      "andaman & nicobar islands": "Andaman & Nicobar Island",
      "andaman & nicobar": "Andaman & Nicobar Island",
      "andaman and nicobar": "Andaman & Nicobar Island",
      "andhra pradesh": "Andhra Pradesh",
      "arunachal pradesh": "Arunachal Pradesh",
      assam: "Assam",
      bihar: "Bihar",
      chandigarh: "Chandigarh",
      chhattisgarh: "Chhattisgarh",
      delhi: "Delhi",
      goa: "Goa",
      gujarat: "Gujarat",
      haryana: "Haryana",
      "himachal pradesh": "Himachal Pradesh",
      "jammu & kashmir": "Jammu & Kashmir",
      "jammu and kashmir": "Jammu & Kashmir",
      jharkhand: "Jharkhand",
      karnataka: "Karnataka",
      kerala: "Kerala",
      lakshadweep: "Lakshadweep",
      "madhya pradesh": "Madhya Pradesh",
      maharashtra: "Maharashtra",
      manipur: "Manipur",
      meghalaya: "Meghalaya",
      mizoram: "Mizoram",
      nagaland: "Nagaland",
      odisha: "Odisha",
      orissa: "Odisha",
      puducherry: "Puducherry",
      punjab: "Punjab",
      rajasthan: "Rajasthan",
      sikkim: "Sikkim",
      "tamil nadu": "Tamil Nadu",
      telangana: "Telangana",
      tripura: "Tripura",
      "uttar pradesh": "Uttar Pradesh",
      uttarakhand: "Uttarakhand",
      "west bengal": "West Bengal",
    };

    // Transform array to object keyed by state name
    const stateDataObj = salesData.reduce((acc, curr) => {
      let amount = 0;
      if (timeRange === "month") {
        // For monthly view, use total object
        amount = showQuantity
          ? curr.total?.qty || 0
          : curr.total?.net_amount || 0;
      } else {
        // For daily view, use total_net_amount and total_qty
        amount = showQuantity
          ? curr.total_qty || 0
          : curr.total_net_amount || 0;
      }

      // Get state name consistently
      const stateName = timeRange === "month" ? curr.state : curr.stat_value;

      if (stateName && !stateName.toLowerCase().includes("total")) {
        // Normalize state name and map to expected format
        const normalizedStateName = stateName.toLowerCase().trim();
        const mappedStateName = stateNameMapping[normalizedStateName];

        if (mappedStateName) {
          acc[mappedStateName] = {
            value: amount,
          };
          // console.log("Processing state:", mappedStateName, "value:", acc[mappedStateName]);
        } else {
          console.warn("Unknown state name:", stateName);
        }
      }
      return acc;
    }, {});

    console.log("stateDataObj:", stateDataObj);

    // Update color thresholds to match the legend
    const getColor = (value) => {
      if (showQuantity) {
        // For quantity
        if (value >= 1000) return "#0ea5e9";
        if (value >= 500) return "#38bdf8";
        if (value >= 100) return "#7dd3fc";
        if (value >= 50) return "#bae6fd";
        return "#e0f2fe";
      } else {
        // For amount (in rupees)
        if (value >= 1000000) return "#0ea5e9"; // ₹10L+
        if (value >= 500000) return "#38bdf8"; // ₹5L+
        if (value >= 100000) return "#7dd3fc"; // ₹1L+
        if (value >= 50000) return "#bae6fd"; // ₹50K+
        return "#e0f2fe"; // ₹0-50K
      }
    };

    const mapData = Object.entries(stateDataObj).reduce(
      (acc, [state, data]) => {
        acc[state] = {
          value: data.value,
          color: getColor(data.value),
        };
        return acc;
      },
      {}
    );

    console.log("Final mapData:", mapData);

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">
          State-wise Sales Distribution
        </h3>

        <div
          className="h-[400px] m-auto  max-w-md flex items-center justify-center"
          style={{ position: "relative" }}
        >
          <div className="w-full max-w-md">
            <IndiaMap
              style={{ position: "relative", left: "25%" }}
              regionData={mapData}
              hoverComponent={({ value }) => {
                return (
                  <div>
                    <div>
                      <p className="font-semibold">{value.name}</p>
                      <p className="text-sm">
                        {showQuantity ? "Quantity: " : "Sales: "}
                        {showQuantity
                          ? value.value ?? 0
                          : formatAmount(value.value ?? 0)}
                      </p>
                    </div>
                  </div>
                );
              }}
              mapLayout={{
                title: "",
                legendTitle: "",
                startColor: "#e0f2fe",
                endColor: "#0ea5e9",
                hoverTitle: "Count",
                noDataColor: "#e0f2fe",
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="inline-block ml-1 text-gray-400">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 2L9 6H3L6 2Z" />
            <path d="M6 10L3 6H9L6 10Z" opacity="0.4" />
          </svg>
        </span>
      );
    }

    if (sortDirection === "asc") {
      return (
        <span className="inline-block ml-1 text-blue-600">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 2L9 6H3L6 2Z" />
          </svg>
        </span>
      );
    } else {
      return (
        <span className="inline-block ml-1 text-blue-600">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 10L3 6H9L6 10Z" />
          </svg>
        </span>
      );
    }
  };

  const sortData = (data, field, direction) => {
    if (!field) return data;

    // Separate grand total rows from regular data
    const grandTotalRows = data.filter((item) => {
      const name = (item.state || item.stat_value || "").toLowerCase();
      return name.includes("total") || name.includes("grand");
    });

    const regularData = data.filter((item) => {
      const name = (item.state || item.stat_value || "").toLowerCase();
      return !(name.includes("total") || name.includes("grand") || name === "n/a" || name === "na");
    });

    // Sort only the regular data
    const sortedRegularData = [...regularData].sort((a, b) => {
      let aVal, bVal;

      if (field === "state") {
        // For state name column
        aVal = (a.state || a.stat_value || "").toLowerCase();
        bVal = (b.state || b.stat_value || "").toLowerCase();
      } else if (field === "total") {
        if (timeRange === "month") {
          aVal = showQuantity ? a.total?.qty || 0 : a.total?.net_amount || 0;
          bVal = showQuantity ? b.total?.qty || 0 : b.total?.net_amount || 0;
        } else {
          aVal = showQuantity ? a.total_qty || 0 : a.total_net_amount || 0;
          bVal = showQuantity ? b.total_qty || 0 : b.total_net_amount || 0;
        }
      } else {
        // For month/date columns
        if (timeRange === "month") {
          aVal = showQuantity ? a[field]?.qty || 0 : a[field]?.net_amount || 0;
          bVal = showQuantity ? b[field]?.qty || 0 : b[field]?.net_amount || 0;
        } else {
          const aTimeData = a.daily_data;
          const bTimeData = b.daily_data;
          aVal = showQuantity
            ? aTimeData?.[field]?.qty || 0
            : aTimeData?.[field]?.net_amount || 0;
          bVal = showQuantity
            ? bTimeData?.[field]?.qty || 0
            : bTimeData?.[field]?.net_amount || 0;
        }
      }

      if (direction === "asc") {
        if (typeof aVal === "string") return aVal.localeCompare(bVal);
        return aVal - bVal;
      } else {
        if (typeof aVal === "string") return bVal.localeCompare(aVal);
        return bVal - aVal;
      }
    });

    // Combine sorted regular data with grand total rows at the end
    return [...sortedRegularData, ...grandTotalRows];
  };

  const renderTable = () => {
    if (!salesData) return null;
    const sales = sortData(salesData, sortField, sortDirection);
    if (timeRange === "month") {
      // Generate monthList between startDate and endDate
      const monthList = [];
      let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      while (current <= end) {
        monthList.push({
          key: `${current.getFullYear()}-${current.getMonth() + 1}`,
          label: current.toLocaleString("default", { month: "short" }),
          year: current.getFullYear(),
          month: current.getMonth(),
        });
        current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
      }
      return (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("state")}
                >
                  <div className="flex items-center">
                    State
                    {getSortIcon("state")}
                  </div>
                </th>
                {monthList.map((m) => (
                  <th
                    key={m.key}
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort(m.label)}
                  >
                    <div className="flex items-center">
                      {m.label} {m.year}
                      {getSortIcon(m.label)}
                    </div>
                  </th>
                ))}
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("total")}
                >
                  <div className="flex items-center">
                    Total
                    {getSortIcon("total")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale, index) => {
                const isGrandTotal =
                  sale.state?.toLowerCase().includes("total") ||
                  sale.state?.toLowerCase().includes("grand");
                return (
                  <tr
                    key={index}
                    className={isGrandTotal ? "font-bold" : ""}
                    style={
                      isGrandTotal
                        ? { backgroundColor: "#e0f2fe", color: "#0c4a6e" }
                        : {}
                    }
                  >
                    <td
                      className="whitespace-nowrap capitalize transition-colors duration-150 cursor-pointer hover:bg-blue-100 hover:text-blue-700"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (sale.state && !isGrandTotal) {
                          const params = new URLSearchParams();
                          params.append("state", sale.state);
                          params.append(
                            "startDate",
                            startDate.toLocaleDateString("en-CA").split("T")[0]
                          );
                          params.append(
                            "endDate",
                            endDate.toLocaleDateString("en-CA").split("T")[0]
                          );
                          navigate(
                            `/analytics/product-by-state?${params.toString()}`,
                            {
                              state: {
                                fromPage: "/analytics/state-wise-sales-trend",
                                startDate:
                                  startDate.toLocaleDateString("en-CA"),
                                endDate: endDate.toLocaleDateString("en-CA"),
                                timeRange: timeRange,
                                postingAccount: postingAccount,
                                showQuantity: showQuantity,
                                showAllStates: showAllStates,
                              },
                            }
                          );
                        }
                      }}
                    >
                      {sale.state}
                    </td>
                    {monthList.map((m) => {
                      const key = m.label;
                      return <td key={m.key}>{formatCell(sale[key])}</td>;
                    })}
                    <td>{formatCell(sale.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else {
      // For daily view
      const dates = Object.keys(sales[0]?.daily_data || {})
        .sort((a, b) => new Date(a) - new Date(b))
        .map((date) => {
          const d = new Date(date);
          return {
            fullDate: date,
            displayDate: `${d.getDate()}-${d.toLocaleString("default", {
              month: "short",
            })}`,
          };
        });
      return (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("state")}
                >
                  <div className="flex items-center">
                    State
                    {getSortIcon("state")}
                  </div>
                </th>
                {dates.map(({ fullDate, displayDate }) => (
                  <th
                    key={fullDate}
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort(fullDate)}
                  >
                    <div className="flex items-center">
                      {displayDate}
                      {getSortIcon(fullDate)}
                    </div>
                  </th>
                ))}
                <th
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort("total")}
                >
                  <div className="flex items-center">
                    Total
                    {getSortIcon("total")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale, index) => {
                const isGrandTotal =
                  sale.stat_value?.toLowerCase().includes("total") ||
                  sale.stat_value?.toLowerCase().includes("grand");
                return (
                  <tr
                    key={index}
                    className={isGrandTotal ? "!text-white font-bold" : ""}
                    style={isGrandTotal ? { backgroundColor: "#1f2937" } : {}}
                  >
                    <td
                      className="whitespace-nowrap capitalize transition-colors duration-150 cursor-pointer hover:bg-blue-100 hover:text-blue-700"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (sale.stat_value && !isGrandTotal) {
                          const params = new URLSearchParams();
                          params.append("state", sale.stat_value);
                          params.append(
                            "startDate",
                            startDate.toLocaleDateString("en-CA").split("T")[0]
                          );
                          params.append(
                            "endDate",
                            endDate.toLocaleDateString("en-CA").split("T")[0]
                          );
                          navigate(
                            `/analytics/product-by-state?${params.toString()}`,
                            {
                              state: {
                                fromPage: "/analytics/state-wise-sales-trend",
                                startDate:
                                  startDate.toLocaleDateString("en-CA"),
                                endDate: endDate.toLocaleDateString("en-CA"),
                                timeRange: timeRange,
                                postingAccount: postingAccount,
                                showQuantity: showQuantity,
                                showAllStates: showAllStates,
                              },
                            }
                          );
                        }
                      }}
                    >
                      {sale.stat_value}
                    </td>
                    {dates.map(({ fullDate }) => (
                      <td key={`${sale.stat_value}-${fullDate}`}>
                        {formatCell(sale.daily_data?.[fullDate])}
                      </td>
                    ))}
                    <td>{formatAmount(sale.total_net_amount ?? 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
  };

  if (isSalesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <h1 className="text-2xl font-bold">State Wise Sales Trend</h1>
        <div className="flex flex-wrap gap-4 items-center w-full lg:w-auto">
        <select
            className="select select-bordered w-full lg:w-40"
            value={postingAccount}
            onChange={(e) => setPostingAccount(e.target.value)}
          >
            <option value="">All</option>
            <option value="amazon">Amazon</option>
            <option value="flipkart">Flipkart</option>
            <option value="shopify">Shopify</option>
            <option value="tata 1mg">Tata 1mg</option>
            <option value="palsons derma pvt ltd">Palsons Derma Pvt Ltd</option>
          </select>
          <div className="flex gap-2 items-center w-full lg:w-auto">
            <DatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              className="text-sm input input-bordered w-full lg:w-40"
              dateFormat={timeRange === "month" ? "yyyy-MM" : "yyyy-MM-dd"}
              showMonthYearPicker={timeRange === "month"}
              showFullMonthYearPicker={timeRange === "month"}
              minDate={
                timeRange === "month"
                  ? new Date(endDate.getFullYear(), endDate.getMonth() - 11, 1)
                  : new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000)
              }
              maxDate={endDate}
            />
            <DatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              className="text-sm input input-bordered w-full lg:w-40"
              dateFormat={timeRange === "month" ? "yyyy-MM" : "yyyy-MM-dd"}
              showMonthYearPicker={timeRange === "month"}
              showFullMonthYearPicker={timeRange === "month"}
              maxDate={new Date()}
            />
          </div>

          <div className="btn-group">
            <button
              className={`btn btn-sm ${timeRange === "day" ? "btn-primary" : "btn-ghost"
                }`}
              onClick={() => setTimeRange("day")}
            >
              Daily
            </button>
            <button
              className={`btn btn-sm ${timeRange === "month" ? "btn-primary" : "btn-ghost"
                }`}
              onClick={() => setTimeRange("month")}
            >
              Monthly
            </button>
          </div>

    

          

          <button
            className="btn btn-primary btn-sm"
            onClick={handleExportToCSV}
            disabled={isSalesLoading || isExporting}
          >
            {isSalesLoading || isExporting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              "Export"
            )}
          </button>
        </div>
      </div>

      {/* Charts Grid - Horizontal  and Indian map side by side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {renderHorizontalBarChart()}
        {renderIndiaMap()}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold mb-4">
            State Wise Sales Trend 
          </h3>
          <div className="form-control">
            <label className="label cursor-pointer">
             
              <input
                type="checkbox"
                className="toggle toggle-primary"
                checked={showQuantity}
                onChange={(e) => setShowQuantity(e.target.checked)}
              />
               <span className="label-text ml-2"> Show Sales Count</span>
            </label>
          </div>
          </div>
         
          <div className="overflow-x-auto">{renderTable()}</div>
        </div>
      </div>
    </div>
  );
};

export default StateWiseSalesTrend;
